import React, { useEffect, useState, Fragment } from 'react';
import Layout from 'components/sales/layout/layout';
import localization from 'assets/localization/localization';
import loaderGIF from 'assets/images/imageIcons/purple-loader.gif';

const CompressImage = ({ imageCompressUrl }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileExtension, setFileExtension] = useState('');
  const [compressedImageUrl, setCompressedImageUrl] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const extension = file.name.split('.').pop();
      setFileExtension(extension);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (selectedFile) {
      try {
        const response = await imageCompressUrl(selectedFile, fileExtension);
        if (response) {
          setCompressedImageUrl(response);
          setError('');
        }
      } catch (error) {
        console.error('Error compressing image:', error);
        setError('Something went wrong while compressing the image. Please try again.');
      } finally {
        setLoading(false);
      }
    } else {
      setError('No file selected');
      setLoading(false);
    }
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(compressedImageUrl);
    setShowToast(true);
  };

  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [showToast]);

  const handleCompressMore = () => {
    setSelectedFile(null);
    setFileExtension('');
    setCompressedImageUrl('');
    setError('');
    const fileInput = document.getElementById('fileUpload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  return (
    <Layout title={localization.imageCompression}>
      <div style={styles.container}>
        <div style={styles.card}>
          <h2 style={styles.header}>Image Compression</h2>
          <form className="compress-image-form" onSubmit={handleSubmit} style={styles.form}>
            <div className="form-group" style={styles.formGroup}>
              <label htmlFor="fileUpload" style={styles.label}>Upload Image</label>
              <input 
                type="file" 
                id="fileUpload" 
                accept=".jpg,.png"
                onChange={handleImageChange}
                style={styles.input}
                disabled={!!compressedImageUrl}
              />
            </div>
            <button 
              type="submit" 
              className="btn btn-primary" 
              style={compressedImageUrl ? styles.buttonDisabled : styles.button}
              disabled={!!compressedImageUrl}
            >
              Compress Image
            </button>
          </form>
          {loading && (
            <div>
              <img src={loaderGIF} alt="Loading..." />
            </div>
          )}          
          {error && <div style={styles.error}>{error}</div>}
          {compressedImageUrl && (
            <div style={styles.resultContainer}>
              <div style={styles.urlContainer}>
                <span style={styles.urlLabel}><strong>Compressed Image URL:</strong></span>
                <span style={styles.urlText}>{compressedImageUrl}</span>
                <button onClick={handleCopyToClipboard} style={styles.copyButton}>
                  <img width="30" height="30" src="https://img.icons8.com/ios-glyphs/30/FFFFFF/copy.png" alt="copy"/>                
                </button>
              </div>
              <button onClick={handleCompressMore} style={styles.compressMoreButton}>Compress More</button>
            </div>
          )}
        </div>
      </div>
      {showToast && (
        <div style={styles.toast}>
          URL copied to clipboard!
        </div>
      )}
    </Layout>
  );
};

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    height: '100vh',
    paddingTop: '40px',
    backgroundColor: '#f8f8ff',
  },
  card: {
    backgroundColor: '#dcd6f7',
    padding: '50px',
    borderRadius: '16px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
    width: '600px',
    textAlign: 'center',
  },
  header: {
    marginBottom: '30px',
    color: '#4b0082',
    fontSize: '24px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    color: '#4b0082',
    marginBottom: '10px',
    fontSize: '18px',
  },
  input: {
    padding: '10px',
    borderRadius: '8px',
    border: '1px solid #bbb',
    width: '100%',
  },
  button: {
    padding: '12px 20px',
    backgroundColor: '#4b0082',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    marginTop: '20px',
    alignSelf: 'center',
    width: 'fit-content',
  },
  buttonDisabled: {
    padding: '12px 20px',
    backgroundColor: '#bbbbbb',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'not-allowed',
    fontSize: '16px',
    marginTop: '20px',
    alignSelf: 'center',
    width: 'fit-content',
  },
  resultContainer: {
    marginTop: '20px',
  },
  urlContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: '8px',
    color: '#4b0082',
    marginBottom: '20px',
    width: '100%',
    boxSizing: 'border-box',
    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.1)',
  },
  urlLabel: {
    fontSize: '16px',
    marginBottom: '10px',
  },
  urlText: {
    fontSize: '16px',
    marginBottom: '10px',
    fontFamily: 'monospace',
    wordBreak: 'break-all',
  },
  copyButton: {
    padding: '8px 16px',
    backgroundColor: '#4b0082',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  compressMoreButton: {
    padding: '12px 20px',
    backgroundColor: '#4b0082',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    width: '100%',
  },
  error: {
    marginTop: '20px',
    color: 'red',
    fontSize: '14px',
  },
  toast: {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    backgroundColor: 'white',
    color: 'green',
    padding: '15px 30px',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    zIndex: 1000,
    transition: 'all 0.5s ease-in-out',
  },
};

export default CompressImage;